import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  Select,
  ActionButtonsRow,
  Result,
  MathDiv,
  PageContainer,
  PageHeader,
  BackButton,
  Badge,
  InfoCard,
  CopyHTMLButton,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'

export function CFSteelBending() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [profiles, setProfiles] = useState([])
  const [catalogs, setCatalogs] = useState([])
  const [fy, setFy] = useState(240)
  const [e, setE] = useState(200000)
  const [kxlx, setKxlx] = useState(250)
  const [kyly, setKyly] = useState(250)
  const [kzlz, setKzlz] = useState(250)
  const [isFormValid, setIsFormValid] = useState(false)
  const [selectedCatalogId, setSelectedCatalogId] = useState('')
  const [selectedProfileType, setSelectedProfileType] = useState('UE')
  const [selectedProfile, setSelectedProfile] = useState(null)
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [touched, setTouched] = useState({})
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  useEffect(() => {
    async function fetchProfiles() {
      const [cRes, uRes, uNbrRes] = await Promise.all([
        api.get('/cprofiles'),
        api.get('/uprofiles'),
        api.get('/uprofiles-nbr-15253'),
      ])
      const cData = cRes.data
      const uData = uRes.data
      const uNbrData = uNbrRes.data
      setCatalogs([cData, uData, uNbrData])
      setProfiles(cData.profiles)
      setSelectedCatalogId(cData.id)
      setSelectedProfileType(cData.type)
      setSelectedProfile(cData.profiles[0])
    }
    fetchProfiles()
  }, [])

  const HISTORY_KEY = 'cf-steel-bending-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }
    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return
      const entry = history[index]
      setFy(entry.fy)
      setE(entry.e)
      setKxlx(entry.kxlx)
      setKyly(entry.kyly)
      setKzlz(entry.kzlz)
      setSelectedProfileType(entry.selectedProfileType)
      setSelectedProfile(entry.selectedProfile)
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) return 'Campo obrigatório'
    const numValue = Number(value)
    if (isNaN(numValue)) return 'Valor deve ser um número'
    if (numValue <= 0) return 'Valor deve ser maior que 0'
    return null
  }, [])

  useEffect(() => {
    const newErrors = {}
    newErrors.fy = validateField('fy', fy)
    newErrors.e = validateField('e', e)
    newErrors.kxlx = validateField('kxlx', kxlx)
    newErrors.kyly = validateField('kyly', kyly)
    newErrors.kzlz = validateField('kzlz', kzlz)
    setErrors(newErrors)
    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [fy, e, kxlx, kyly, kzlz, validateField])

  const handleProfileChange = (event) => {
    setSelectedProfile(profiles.find((p) => p.name === event.target.value))
  }

  const handleProfileTypeChange = (event) => {
    const id = event.target.value
    const catalog = catalogs.find((c) => c.id === id)
    if (catalog) {
      setSelectedCatalogId(catalog.id)
      setSelectedProfileType(catalog.type)
      setProfiles(catalog.profiles)
      setSelectedProfile(catalog.profiles[0])
    }
  }

  async function handleCalculate(event) {
    event.preventDefault()
    const hasExistingResults = result !== ''
    setIsLoading(true)
    if (hasExistingResults) setIsRecalculating(true)

    try {
      const payload = {
        kxlx_cm: kxlx,
        kyly_cm: kyly,
        kzlz_cm: kzlz,
        material: {
          fy_mpa: fy,
          e_mpa: e,
        },
        profile: { name: selectedProfile.name },
      }

      const response = await api.post('/cf-steel/bending', payload)
      setResult(response.data)
      setData(payload)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fy,
        e,
        kxlx,
        kyly,
        kzlz,
        selectedProfileType,
        selectedProfile,
        result: response.data,
        data: payload,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)
      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }
      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) setTimeout(() => setIsRecalculating(false), 500)
    }
  }

  function handleSave() {
    const stateData = { fy, e, kxlx, kyly, kzlz, selectedProfileType, selectedProfile, data, result, isFormValid }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Aço Frio Flexão.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)
        setFy(jsonData.fy)
        setE(jsonData.e)
        setKxlx(jsonData.kxlx)
        setKyly(jsonData.kyly)
        setKzlz(jsonData.kzlz)
        setSelectedProfileType(jsonData.selectedProfileType)
        setSelectedProfile(jsonData.selectedProfile)
        setResult(jsonData.result)
        setData(jsonData.data)
        setIsFormValid(jsonData.isFormValid)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({ result })
  }

  const fyKn = data.material ? (data.material.fy_mpa / 10).toFixed(1) : ''

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #2c3e50 0%, #34495e 100%)">
        <div className="header-content">
          <BackButton to="/aco">
            <FiArrowLeft /> Voltar para Aço
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/aco">Aço</a>
            <span>/</span>
            <span>Flexão de perfil formado à frio</span>
          </div>
          <h1>🔩 Flexão de perfil formado à frio</h1>
          <p className="subtitle">
            Dimensionamento e verificação de perfis de aço formados a frio submetidos à flexão simples
          </p>
          <div className="badges">
            <Badge>📋 ABNT NBR 14762</Badge>
            <Badge>🧮 Método das Seções Efetivas</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #e8f4f8 0%, #cfe9f3 100%)"
        $border="2px solid #a8d8ea"
        $primaryColor="#2c3e50"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta realiza o dimensionamento de perfis de aço formados a frio submetidos à flexão simples,
              conforme os critérios da ABNT NBR 14762.
            </p>
            <p>
              O cálculo aplica o Método das Seções Efetivas (MSE), considerando a instabilidade local da alma e a
              flambagem lateral com torção (FLT), determinando o momento resistente de cálculo do perfil selecionado.
            </p>
            <div className="norms">
              <span>ABNT NBR 14762</span>
              <span>Perfis Formados a Frio</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />
          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <h3>Materiais</h3>
          <form onSubmit={handleCalculate}>
            <InputDiv>
              <span>fy (MPa)</span>
              <ValidatedInput
                name="fy"
                type="number"
                value={fy || ''}
                onChange={(event) => setFy(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>E (MPa)</span>
              <ValidatedInput
                name="e"
                type="number"
                value={e || ''}
                onChange={(event) => setE(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Seleção do perfil</h3>
            <InputDiv>
              <span>Tipo</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  id="profileTypeSelect"
                  value={selectedCatalogId}
                  onChange={handleProfileTypeChange}
                >
                  {catalogs.map((catalog) => (
                    <option key={catalog.id} value={catalog.id}>
                      {catalog.name}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            {profiles.length > 0 && selectedProfile && (
              <InputDiv>
                <span>Perfil</span>
                <div>
                  <Select
                    style={{ marginLeft: '0' }}
                    id="profileSelect"
                    value={selectedProfile.name}
                    onChange={handleProfileChange}
                  >
                    {profiles.map((profile, index) => (
                      <option key={index} value={profile.name}>
                        {profile.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </InputDiv>
            )}
            <h3>Comprimentos efetivos</h3>
            <InputDiv>
              <span>KxLx (cm)</span>
              <ValidatedInput
                name="kxlx"
                type="number"
                value={kxlx || ''}
                onChange={(event) => setKxlx(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>KyLy (cm)</span>
              <ValidatedInput
                name="kyly"
                type="number"
                value={kyly || ''}
                onChange={(event) => setKyly(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>KzLz (cm)</span>
              <ValidatedInput
                name="kzlz"
                type="number"
                value={kzlz || ''}
                onChange={(event) => setKzlz(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('cf-bending-file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate || !selectedProfile}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="cf-bending-file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>

        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <MathDiv>
                <BlockMath math={`M_{Rd} = ${result.mrd_kncm.toFixed(2)} \\, kN \\cdot cm`} />
              </MathDiv>
            </Result>

            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <MathDiv>
                <h3>Módulo resistente elástico</h3>
                <BlockMath math={`W_c = ${result.wc_cm3.toFixed(3)} \\, cm^3`} />

                <h3>Coeficiente de instabilidade local</h3>
                <BlockMath math={`\\eta = \\dfrac{b_f}{b_w} = ${result.n.toFixed(2)}`} />
                {result.u !== undefined && <BlockMath math={`\\mu = \\dfrac{h_s}{h} = ${result.u.toFixed(2)}`} />}
                <BlockMath math={`k_l = ${result.kl.toFixed(2)}`} />

                <h3>Momento de instabilidade local</h3>
                <BlockMath
                  math={`M_l = \\dfrac{k_l \\cdot \\pi^2 \\cdot E \\cdot W_c}{12 \\cdot (1 - \\nu^2) \\cdot \\left( \\dfrac{b_w}{t} \\right)^2}`}
                />
                <BlockMath math={`M_l = ${result.ml_kncm.toFixed(2)} \\, kN \\cdot cm`} />

                <h3>Seção efetiva — 1ª etapa (sem FLT)</h3>
                <BlockMath math={`\\lambda_p = \\sqrt{\\dfrac{W_c \\cdot f_y}{M_l}}`} />
                <BlockMath
                  math={`\\lambda_p = \\sqrt{\\dfrac{${result.wc_cm3.toFixed(3)} \\cdot ${fyKn}}{${result.ml_kncm.toFixed(2)}}}`}
                />
                <BlockMath math={`\\lambda_p = ${result.lambdaP.toFixed(4)}`} />
                {result.lambdaP <= 0.673 ? (
                  <BlockMath
                    math={`\\lambda_p \\leq 0{,}673 \\therefore W_{ef} = W_c = ${result.wef_cm3.toFixed(3)} \\, cm^3`}
                  />
                ) : (
                  <>
                    <BlockMath
                      math={`\\lambda_p > 0{,}673 \\therefore W_{ef} = W_c \\cdot \\left(1 - \\dfrac{0{,}22}{\\lambda_p}\\right) \\cdot \\dfrac{1}{\\lambda_p}`}
                    />
                    <BlockMath
                      math={`W_{ef} = ${result.wc_cm3.toFixed(3)} \\cdot \\left(1 - \\dfrac{0{,}22}{${result.lambdaP.toFixed(4)}}\\right) \\cdot \\dfrac{1}{${result.lambdaP.toFixed(4)}} = ${result.wef_cm3.toFixed(3)} \\, cm^3`}
                    />
                  </>
                )}
                <BlockMath
                  math={`M_{Rd,1} = \\dfrac{W_{ef} \\cdot f_y}{1{,}1} = \\dfrac{${result.wef_cm3.toFixed(3)} \\cdot ${fyKn}}{1{,}1} = ${result.mrd1_kncm.toFixed(2)} \\, kN \\cdot cm`}
                />

                <h3>Cargas críticas elásticas</h3>
                <BlockMath
                  math={`N_{ex} = \\dfrac{\\pi^2 \\cdot E \\cdot I_x}{(K_x L_x)^2} = ${result.nes.nex_kn.toFixed(2)} \\, kN`}
                />
                <BlockMath
                  math={`N_{ey} = \\dfrac{\\pi^2 \\cdot E \\cdot I_y}{(K_y L_y)^2} = ${result.nes.ney_kn.toFixed(2)} \\, kN`}
                />
                <BlockMath
                  math={`N_{ez} = \\dfrac{\\dfrac{\\pi^2 \\cdot E \\cdot I_w}{(K_z L_z)^2} + G \\cdot I_t}{r_0^2} = ${result.nes.nez_kn.toFixed(2)} \\, kN`}
                />

                <h3>Flambagem Lateral com Torção (FLT)</h3>
                <BlockMath math={`M_e = C_b \\cdot r_0 \\cdot \\sqrt{N_{ey} \\cdot N_{ez}}`} />
                <BlockMath math={`M_e =  ${result.me_kn.toFixed(2)} \\, kN \\cdot cm \\quad (C_b = 1{,}0)`} />
                <BlockMath math={`\\lambda_0 = \\sqrt{\\dfrac{W_c \\cdot f_y}{M_e}}`} />
                <BlockMath
                  math={`\\lambda_0 = \\sqrt{\\dfrac{${result.wc_cm3.toFixed(3)} \\cdot ${fyKn}}{${result.me_kn.toFixed(2)}}}`}
                />
                <BlockMath math={`\\lambda_0 = ${result.lambda0.toFixed(4)}`} />
                {result.lambda0 <= 0.6 ? (
                  <BlockMath math={`\\lambda_0 \\leq 0{,}6 \\therefore \\chi_{FLT} = 1{,}0`} />
                ) : result.lambda0 < 1.336 ? (
                  <>
                    <BlockMath
                      math={`0{,}6 < \\lambda_0 < 1{,}336 \\therefore \\chi_{FLT} = 1{,}1 \\cdot (1 - 0{,}278 \\cdot \\lambda_0^2)`}
                    />
                    <BlockMath
                      math={`\\chi_{FLT} = 1{,}1 \\cdot (1 - 0{,}278 \\cdot ${result.lambda0.toFixed(4)}^2) = ${result.xFLT.toFixed(4)}`}
                    />
                  </>
                ) : (
                  <>
                    <BlockMath math={`\\lambda_0 \\geq 1{,}336 \\therefore \\chi_{FLT} = \\dfrac{1}{\\lambda_0^2}`} />
                    <BlockMath
                      math={`\\chi_{FLT} = \\dfrac{1}{${result.lambda0.toFixed(4)}^2} = ${result.xFLT.toFixed(4)}`}
                    />
                  </>
                )}

                <h3>Seção efetiva — 2ª etapa (com FLT)</h3>
                <BlockMath math={`\\lambda_{p1} = \\sqrt{\\dfrac{\\chi_{FLT} \\cdot W_c \\cdot f_y}{M_l}}`} />
                <BlockMath
                  math={`\\lambda_{p1} = \\sqrt{\\dfrac{${result.xFLT.toFixed(4)} \\cdot ${result.wc_cm3.toFixed(3)} \\cdot ${fyKn}}{${result.ml_kncm.toFixed(2)}}}`}
                />
                <BlockMath math={`\\lambda_{p1} = ${result.lambdaP1.toFixed(4)}`} />
                {result.lambdaP1 <= 0.673 ? (
                  <BlockMath
                    math={`\\lambda_{p1} \\leq 0{,}673 \\therefore W_{c,ef} = W_c = ${result.wef1_cm3.toFixed(3)} \\, cm^3`}
                  />
                ) : (
                  <>
                    <BlockMath
                      math={`\\lambda_{p1} > 0{,}673 \\therefore W_{c,ef} = W_c \\cdot \\left(1 - \\dfrac{0{,}22}{\\lambda_{p1}}\\right) \\cdot \\dfrac{1}{\\lambda_{p1}}`}
                    />
                    <BlockMath
                      math={`W_{c,ef} = ${result.wc_cm3.toFixed(3)} \\cdot \\left(1 - \\dfrac{0{,}22}{${result.lambdaP1.toFixed(4)}}\\right) \\cdot \\dfrac{1}{${result.lambdaP1.toFixed(4)}} = ${result.wef1_cm3.toFixed(3)} \\, cm^3`}
                    />
                  </>
                )}

                <h3>Momento resistente de cálculo</h3>
                <BlockMath math={`M_{Rd} = \\dfrac{\\chi_{FLT} \\cdot W_{c,ef} \\cdot f_y}{1{,}1}`} />
                <BlockMath
                  math={`M_{Rd} = \\dfrac{${result.xFLT.toFixed(4)} \\cdot ${result.wef1_cm3.toFixed(3)} \\cdot ${fyKn}}{1{,}1}`}
                />
                <BlockMath math={`\\boxed{M_{Rd} = ${result.mrd_kncm.toFixed(2)} \\, kN \\cdot cm}`} />
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
